import React from "react";
import { withNavigation } from '../Components/helpers/withNavigation';
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  ButtonGroup,
  Input,
} from 'reactstrap';
import {v4 as uuid} from 'uuid';

import './MainAccordion.css'; // Assuming you have a CSS file for styles
import { TourProvider } from '@reactour/tour';
import { TourBridge, SkipHiddenTourSteps } from '../utils/TourHelpers';
import '../App.css';

import audienceIcon from '../assets/audience.png';
import strategyIcon from '../assets/strategy.png';
import importantIcon from '../assets/important.png';
import stepsIcon from '../assets/steps.png';
import recordIcon from '../assets/record.png';
import reviewIcon from '../assets/review.png';
import exportIcon from '../assets/export.png';
import AudioCapturePopup from "../Components/helpers/AudioCapturePopup";

function MainAccordionWithTour(props){
  const accordionRef = React.useRef(null);
  const steps= [
    {
      selector:'.function-bar',
      content:(
      <>
        <li>Home makes the app go back to selecting trainings on the homepage, where you can choose between trainings you have created to view, edit, or export.</li>
        {!props.supportsFSAccess && (
          <li>Click Download Backup to save a copy of your data (your browser will download a file)</li>
        )}
        <li>Help to show this tour again.</li>
      </>
      ),
    },
    {
      selector: '.communinication-partners-accordion-item',
      content:(
        <>
          <ul>
            <li>List the communication partners who would benefit from the training.</li>
            <li>
               For example,
              <ul>
                <li>Sara’s family (a preschooler who uses AAC)</li>
                <li>Paraprofessionals in an autism-support class</li>
                <li>Healthcare workers</li>
                <li>Direct support workers for Sam (an adult who uses AAC)</li>
                <li>Preservice speech language pathologists or teachers, etc. </li>
              </ul>
            </li>  
          </ul>
        </>
      ),
    },{
      selector: '.strategy-accordion-item',
      content:(
        <>
          <ul>
            <li>State the strategy or key skill that the communication partners should learn.</li>
            <li>This will be the title of the training.</li>
            <li>
              For example,
              <ul>
                <li>Positioning the AAC system correctly</li>
                <li>Offering communication choices</li>
                <li>Using a visual schedule</li>
                <li>Providing aided AAC modeling</li>
              </ul>
            </li>
          </ul>
        </>
      ),
    },
    {
      selector: '.introduction-accordion-item',
      content:(
        <>
          <ul>
            <li>Describe why the strategy or skill is important.</li>
            <li>Explain the benefits for both the AAC user and their communication partners.</li>
            <li>This will increase motivation to learn the strategy or skill.</li>
            <li>
              You can:
              <ul>
                <li>Type in the box to add a short written explanation</li>
                <li>Record a short audio clip to explain the importance</li>
                <li>Add an image to illustrate the strategy or skill.</li>
              </ul>
            </li>
          </ul>
        </>
      ),
    },
    {
      selector: '.checklist-accordion-item',
      content:(
        <>
          <ul>
            <li>List the steps or key points for the strategy.</li>
            <li>Make sure the steps are manageable.</li>
            <li>The best strategies include 3–5 key steps or points. This will make it easy for communication partners to learn.</li>
            <li style={{ marginTop: "1em" }}>
              You can <strong>add</strong>, <strong>edit</strong>, <strong>reorder</strong>, or <strong>delete</strong> steps as needed.
              <ul>
                <li>Add steps using the add step button.</li>
                <li>Edit and delete by first swiping left (on a tablet), right clicking (on a PC), or control + click (on mac) to reveal options</li>
                <li>Reorder steps by using the reorder steps button and clicking the up/down arrows to move a specific step.</li>
              </ul>
            </li>
            <li>Select <strong>Done reordering</strong> when you have the steps listed correctly.</li>
            <li><strong>Record a short audio clip</strong> to preview the steps for the communication partner.</li>
            <li><strong>Add a video model</strong> of someone using the whole strategy (all the steps in sequence).</li>
          </ul>
        </>
      ),
    },
    {
      selector: '.video-model-accordion-item',
      content:(
        <>
          <ul>
            <li><strong>Add a video</strong> to demonstrate each step. The videos show the communication partners what they should do.</li>
            <li>
              To add a video, you can
              <ul>
                <li>Capture a new video in the moment</li>
                <li>Insert a video that you saved by selecting the file.</li>
              </ul>
            </li>
            <li>
              You can
              <ul>
                <li>Record one video that demonstrates all the steps and then edit it into short clips to show each step one at a time</li>
                <li>Record separate videos for each step </li>
              </ul>
            </li>
            <li>You can also <strong>record a short audio clip</strong> to accompany the video model but this may not be necessary.</li>
          </ul>
        </>
      ),
    },
    {
      selector: '.review-accordion-item',
      content:(
        <>
          <ul>
            <li>
              Provide a review for the communication partners to solidify their learning. Record a short audio clip that
              <ul>
                <li>Reviews all the steps in the strategy</li>
                <li>Emphasizes the importance of the strategy</li>
                <li>Explains when communication partners should use the strategy</li>
              </ul>
            </li>
            <li>
              Add a video model of the strategy to demonstrate
              <ul>
                <li>This may be the same video that you used earlier.</li>
                <li>•	Or you may want to add a new video showing strategy use in a different situation. This will help communication partners generalize use to new situations. </li>
              </ul>
            </li>
          </ul>
        </>
      ),
    },
    {
      selector: '.finalize-accordion-item',
      content:(
        <>
          <ul>
            <li>Review the entire training to ensure all sections are complete and accurate.</li>
            <li>Make any necessary changes.</li>
            <li>Finalize the training.</li>
            <li>Select the training from the HOME page to use it to teach communication partners.</li>
            <li>Export it for use on a different device. This step prepares the training for deployment to different devices as needed to train communication partners. Ensure all sections are complete before exporting.</li>
          </ul>
        </>
      ),
    }
  ]

  return (
    <div className={'training-container'}>
      <TourProvider 
        steps={steps} 
        padding={10} 
        showBadge={false}
        scrollSmooth 
        className="my-custom-tour"  
        onClickClose={() => {props.updateState({showTour: false});}} 
        onClickMask={() => {props.updateState({showTour: false});}} 
      >
        <SkipHiddenTourSteps/>
        <TourBridge showTour={props.showTour} incognitoBrowser={props.incognitoBrowser} updateState={props.updateState} accordionRef={accordionRef}>
          <MainAccordion ref={accordionRef} {...props} />
        </TourBridge>
      </TourProvider>
    </div>
  );
}

class MainAccordion extends React.Component {
  constructor(props) {
    super(props);

    var introductionStep = this.props.currentTraining?.Steps?.find(step => step.StepType === 2 && step.InfoSlideType === 1);
    if(!introductionStep) {
      introductionStep = {
        id: uuid(),
        Index: 0,
        Title: this.props.currentTraining?.Title,
        Information: '',
        StepType: 2,
        InfoSlideType: 1,
      };
    }

    this.inputRefs = [];

    var currentTab = '1';
    if(this.props.currentStep){
      switch(this.props.currentStep.StepType){
        case 2: // Introduction or Conclusion
          if(this.props.currentStep.InfoSlideType === 1)
            currentTab = '3'; // Introduction
          else
            currentTab = '99';
          break;
        case 3: // Checklist step
          if(this.props.currentStep.Title === 'Preview Checklist')
            currentTab = '4'; // Preview checklist
          else if(this.props.currentStep.Title === 'Review Checklist')
            currentTab = '99';  // Review checklist
          else{
            currentTab = (this.props.currentStep.Step + 4).toString(); // Some checklist step
          }
          break;
        case 4: // Video model step
          if(this.props.currentStep.Title === 'Preview Checklist')
            currentTab = '4'; // Preview checklist
          else if(this.props.currentStep.Title === 'Review Checklist')
            currentTab = '99';  // Review checklist
          else{
            currentTab = (this.props.currentStep.Step + 4).toString(); // Some checklist step
          }
          break;
          default:
            currentTab = '1';
          break;
      }
    }

    const saveMessage = {
      icon:'success',
      title:'Progress Saved',
      label: '',
      message: '',
      duration: 3000
    };

    this.state = {
      openedTab: currentTab, // Default to first tab open
      introductionStep: introductionStep,
      draggingId: null,
      dragStartX: null,
      dragStartSwipedId: null,
      saveMessage: saveMessage
    };

    this.audioRef = React.createRef();
    this.imageRef = React.createRef();
    this.videoRef = React.createRef();

    window.handleTextChange = this.handleTextChange.bind(this);
    window.handleCreate = this.handleCreate.bind(this);
    window.openMediaCapture = this.openMediaCapture.bind(this);
  }

  toggle = (id) => {
    var currentStep;
    if(this.state.openedTab === id)
      currentStep = undefined;
    else{
      if(this.state.openedTab === '3' && id !== '3') // Leaving introduction tab
      {
        this.setState({
          introductionStep: this.props.currentStep
        });
      }
      const numericId = Number(id);
      switch(numericId){
        case 3: // Introduction
          currentStep = this.props.currentTraining?.Steps?.find(step => step.Id === this.state.introductionStep.Id) || this.state.introductionStep;
          break;
        case 4: // Preview checklist
          currentStep = this.props.currentTraining?.Steps?.find(step => step.StepType === 3 && step.Title === 'Preview Checklist');
          if(!currentStep){
            currentStep = {
              id: uuid(),
              Index: this.state.introductionStep?.Index + 1 ?? 0,
              Title: 'Preview Checklist',
              Step: 0,
              StepType: 3,
            };
          }
          break;
        case 99: // Review checklist
          currentStep = this.props.currentTraining?.Steps?.find(step => step.StepType === 3 && step.Title === 'Review Checklist');
          if(!currentStep){
            const highestIndex = this.props.currentTraining?.Steps?.reduce((max, step) => {
              return step.Index > max ? step.Index : max;
            }, -1);
            const newIndex = highestIndex ? highestIndex + 1 : 99;
            const newStep = this.props.currentTraining?.CheckList?.length + 1 ?? 1;
            currentStep = {
              id: uuid(),
              Index: newIndex,
              Title: 'Review Checklist',
              Step: newStep,
              StepType: 3,
            };
          }
          break;
        default: // Some step
          if(id >= 5) {
            var checklist = this.props.currentTraining?.CheckList;
            if(checklist && checklist.length >= (id - 5)) {
              var checklistItem = checklist[id - 5];
              if(checklistItem){
                currentStep = this.props.currentTraining?.Steps?.find(step => step.StepType === 3 && step.Step === checklistItem.Index && step.Title === checklistItem.Step);
                if(!currentStep){
                  const earlierVideoModels = this.props.currentTraining?.Steps?.filter(step => 
                    step.StepType === 4 && (step.Step < checklistItem.Index || step.Title === 'Preview Checklist')).length;
                  const higherStep = this.props.currentTraining?.Steps?.find(step => step.StepType === 3 && step.Step > checklistItem.Index);
                  let newIndex = (id - 5) + 2 + earlierVideoModels;
                  if(higherStep && higherStep.Index < newIndex){
                    newIndex = higherStep.Index;
                  }
                  currentStep = {
                    id: uuid(),
                    Index: newIndex,
                    Title: checklistItem.Step,
                    Step: checklistItem.Index,
                    StepType: 3,
                  };
                }
              }
            }
          }
          break;
      }
    }
    this.setState({
      openedTab: this.state.openedTab === id ? '' : id
    });
    this.props.updateState({currentStep, tempMessage: this.state.saveMessage});
  };

  handleTextChange = (event) => {
    console.log(`handleTextChange Field: ${event.target.name} Value: ${event.target.value}`);
    switch(event.target.name) {
      case 'IntroductionText':
        var updatedIntroductionStep = this.state.introductionStep;
        updatedIntroductionStep.Information = event.target.value;
        this.setState({
          introductionStep: updatedIntroductionStep
        });
        this.props.updateState({currentStep: updatedIntroductionStep});
      break;
      default:
        this.props.updateState((prev) => ({ 
          currentTraining: {
            ...prev.currentTraining,
            [event.target.name]: event.target.value
          }
        }));
      break;
    }
  };

  viewPreview = () => {
    const curURL = new URL(window.location.href);
    this.props.navigate({
      pathname: '/information-step',
      search: curURL.search
    });
  };

  openMediaCapture = () => {
    var step = null;
    switch(this.state.openedTab) {
      case '3':
        step = this.props.currentTraining?.Steps?.find(step => step.Id === this.state.introductionStep.Id) || this.state.introductionStep;
      break;
      case '4':
        step = this.props.currentTraining?.Steps?.find(step => step.StepType === 4 && step.Title === 'Preview Checklist');
        if(!step){
          step = {
            id: uuid(),
            Index: this.props.currentStep.Index + 1,
            Title: this.props.currentStep.Title,
            Step: this.props.currentStep.Step,
            StepType: 4,
          };
        }
      break;
      case '99':
        step = this.props.currentTraining?.Steps?.find(step => step.StepType === 4 && step.Title === 'Review Checklist');
        if(!step){
          step = {
            id: uuid(),
            Index: this.props.currentStep.Index + 1,
            Title: this.props.currentStep.Title,
            Step: this.props.currentStep.Step,
            StepType: 4,
          };
        }
      break;
      default:
        if(this.state.openedTab >= 5) {
          var checklist = this.props.currentTraining?.CheckList;
          if(checklist && checklist.length >= (this.state.openedTab - 5)) {
            var checklistItem = checklist[this.state.openedTab - 5];
            step = this.props.currentTraining?.Steps?.find(step => step.StepType === 4 && step.Step === checklistItem.Index && step.Title === checklistItem.Step);
            if(!step){
              step = {
                id: uuid(),
                Index: this.props.currentStep.Index + 1,
                Title: this.props.currentStep.Title,
                Step: this.props.currentStep.Step,
                StepType: 4,
              };
            }
          }
        }
        break;
    }
    if(step){
      const curURL = new URL(window.location.href);
      this.props.updateState({ currentStep: step }, () => {
        if(this.props.currentStep.StepType === 2)
          this.props.navigate({
            pathname: '/camera',
            search: curURL.search
          });
        else
          this.props.navigate({
            pathname: '/video-model-step',
            search: curURL.search
          });
      });
    }
  };

  componentDidMount() {
    // Add delegation listeners to catch events from all items, even those outside viewport
    document.addEventListener('pointerdown', this.handleDelegatedPointerDown, true);
    document.addEventListener('pointermove', this.handleDelegatedPointerMove, true);
    document.addEventListener('pointerup', this.handleDelegatedPointerUp, true);
  }

  componentWillUnmount() {
    document.removeEventListener('pointerdown', this.handleDelegatedPointerDown, true);
    document.removeEventListener('pointermove', this.handleDelegatedPointerMove, true);
    document.removeEventListener('pointerup', this.handleDelegatedPointerUp, true);
  }

  handleDelegatedPointerDown = (e) => {
    const item = e.target.closest('.step-item');
    if (!item) return;
    
    const id = this.getItemIdFromElement(item);
    if (!id) return;
    
    this.handleTouchStart(id, e);
  }

  handleDelegatedPointerMove = (e) => {
    const item = e.target.closest('.step-item');
    if (!item) return;
    
    const id = this.getItemIdFromElement(item);
    if (!id) return;
    
    this.handleTouchMove(id, e);
  }

  handleDelegatedPointerUp = (e) => {
    const item = e.target.closest('.step-item');
    // if (!item) return;
    
    const id = item ? this.getItemIdFromElement(item) : null;
    // if (!id) return;
    
    this.handleTouchEnd(id, e);
  }

  getItemIdFromElement = (element) => {
    // Get the key from the step-item by finding it in the checklist
    const stepContent = element.querySelector('.step-content');
    if (!stepContent) return null;
    
    const text = stepContent.textContent;
    const match = text.match(/^\d+\.\s+(.+)$/);
    if (!match) return null;
    
    const stepTitle = match[1];
    const item = this.props.currentTraining?.CheckList?.find(i => i.Step === stepTitle);
    return item?.Id || null;
  }

  handleTouchStart = (Id, e) => {
    // console.log("Touch start on item:", Id, " event:", e);
    if(this.state.reordering){ return;}
    
    // Capture pointer to ensure events continue to fire even during scroll
    if (e.type === "pointerdown" && e.target.setPointerCapture) {
      try {
        e.target.setPointerCapture(e.pointerId);
      } catch (err) {
        console.warn("setPointerCapture failed:", err);
      }
    }
    
    // Capture pointer to ensure events continue to fire even during scroll
    if (e.type === "pointerdown" && e.currentTarget.setPointerCapture) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (err) {
        console.warn("setPointerCapture failed:", err);
      }
    }
    
    const eventInfo = {
      Id: Id,
      type: e.type,
      timestamp: new Date().toLocaleTimeString(),
      clientX: e.clientX || (e.touches && e.touches[0]?.clientX),
      clientY: e.clientY || (e.touches && e.touches[0]?.clientY),
      touchCount: e.touches?.length || 0
    };
    
    const startX = e.clientX || (e.touches && e.touches[0]?.clientX);
    
    if ((e.type === "touchstart" || e.type === "pointerdown") && (e.touches?.length === 1 || e.isPrimary)) {
      this.setState({
        dragStartX: startX,
        draggingId: Id,
        dragStartSwipedId: this.state.swipedId,
        lastTouchEvent: `${eventInfo.Id} using type ${eventInfo.type} at ${eventInfo.timestamp} - X: ${eventInfo.clientX}, Y: ${eventInfo.clientY}`
      });
    } else if (e.type === "mousedown") {
      this.setState({
        dragStartX: e.clientX,
        draggingId: Id,
        dragStartSwipedId: this.state.swipedId,
        lastTouchEvent: `${eventInfo.Id} using type ${eventInfo.type} at ${eventInfo.timestamp} - X: ${eventInfo.clientX}, Y: ${eventInfo.clientY}`
      });
    }
  };

  handleTouchMove = (Id, e) => {
    if (this.state.draggingId) {
      const currentX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
      const deltaX = currentX - this.state.dragStartX;
      // Detect horizontal drag for swipe
      const item = e.target.closest('.step-item');
      const width = item ? item.offsetWidth : window.innerWidth;
      if(Math.abs(deltaX) > width / 10) {
        if (deltaX < 0 && !this.state.swipedId)
          this.setState({ swipedId: this.state.draggingId });
        if(deltaX > 0 && this.state.swipedId) 
          this.setState({ swipedId: null });
      }else{
        this.setState({ swipedId: this.state.dragStartSwipedId });
      }
    // }else{
    //   if(this.state.reordering){ return;}
    //   if ((e.type === "touchmove" || e.type === "pointermove") && (e.touches?.length === 1 || e.isPrimary)) {
    //     const startX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    //     this.setState({
    //       dragStartX: startX,
    //       draggingId: Id,
    //       dragStartSwipedId: this.state.swipedId,
    //     });
    //   }
    }
  }

  handleTouchEnd = (Id, e) => {
      // Release pointer capture
      if (e.type === "pointerup" && e.target.releasePointerCapture) {
        try {
          e.target.releasePointerCapture(e.pointerId);
        } catch (err) {
          console.warn("releasePointerCapture failed:", err);
        }
      }
      
      this.setState({
        dragStartX: null,
        draggingId: null,
        dragStartSwipedId: null
      });
  }

  moveUp = (item) => {
    const list = [...this.props.currentTraining.CheckList];
    const idx = list.findIndex(i => i.Id === item.Id);
    if (idx > 0) {
      [list[idx - 1], list[idx]] = [list[idx], list[idx - 1]];
      
      // Update steps to match new order
      var steps = this.props.currentTraining.Steps;
      var movedCheckListStep = steps.find(s => s.StepType === 3 && s.Step === item.Index && s.Title === item.Step);
      if(movedCheckListStep){
        var movedVideoModelStep = steps.find(s => s.StepType === 4 && s.Step === item.Index && s.Title === item.Step);
        var otherCheckListStep = steps.find(s => s.StepType === 3 && s.Step === item.Index - 1);
        var otherVideoModelStep = steps.find(s => s.StepType === 4 && s.Step === item.Index - 1);
        const movedSpaces = (movedCheckListStep ? 1 : 0) + (movedVideoModelStep ? 1 : 0);
        const otherSpaces = (otherCheckListStep ? 1 : 0) + (otherVideoModelStep ? 1 : 0);
        if (movedCheckListStep){
          movedCheckListStep.Step = list[idx].Index;
          movedCheckListStep.Index -= otherSpaces;
        }
        if (movedVideoModelStep) {
          movedVideoModelStep.Step = list[idx].Index;
          movedVideoModelStep.Index -= otherSpaces;
        }
        if (otherCheckListStep){
          otherCheckListStep.Step = item.Index;
          otherCheckListStep.Index += movedSpaces;
        }
        if (otherVideoModelStep){ 
          otherVideoModelStep.Step = item.Index;
          otherVideoModelStep.Index += movedSpaces;
        }
      }

      // Re-index
      list.forEach((it, i) => it.Index = i + 1);
      const currentTraining = {...this.props.currentTraining, CheckList: list, Steps: steps.sort((a,b) => a.Index - b.Index)};
      this.props.updateState({currentTraining: currentTraining});
    }
  };

  moveDown = (item) => {
    const list = [...this.props.currentTraining.CheckList];
    const idx = list.findIndex(i => i.Id === item.Id);
    if (idx < list.length - 1) {
      [list[idx], list[idx + 1]] = [list[idx + 1], list[idx]];

      // Update steps to match new order
      var steps = this.props.currentTraining.Steps;
      var movedCheckListStep = steps.find(s => s.StepType === 3 && s.Step === item.Index);
      if(movedCheckListStep){
        var movedVideoModelStep = steps.find(s => s.StepType === 4 && s.Step === item.Index);
        var otherCheckListStep = steps.find(s => s.StepType === 3 && s.Step === item.Index + 1);
        var otherVideoModeStep = steps.find(s => s.StepType === 4 && s.Step === item.Index + 1);
        const movedSpaces = (movedCheckListStep ? 1 : 0) + (movedVideoModelStep ? 1 : 0);
        const otherSpaces = (otherCheckListStep ? 1 : 0) + (otherVideoModeStep ? 1 : 0);
        if (movedCheckListStep){
          movedCheckListStep.Step = list[idx].Index;
          movedCheckListStep.Index += otherSpaces;
        }
        if (movedVideoModelStep) {
          movedVideoModelStep.Step = list[idx].Index;
          movedVideoModelStep.Index += otherSpaces;
        }
        if (otherCheckListStep){
          otherCheckListStep.Step = item.Index;
          otherCheckListStep.Index -= movedSpaces;
        }
        if (otherVideoModeStep){ 
          otherVideoModeStep.Step = item.Index;
          otherVideoModeStep.Index -= movedSpaces;
        }
      }

      // Re-index
      list.forEach((it, i) => it.Index = i + 1);
      const currentTraining = {...this.props.currentTraining, CheckList: list, Steps: steps.sort((a,b) => a.Index - b.Index)};
      this.props.updateState({currentTraining: currentTraining});
    }
  };

  handleEdit = (item) => {
    const newTitle = prompt(`Update Step ${item.Index} Title:`, item.Step);
    if (!newTitle) return; // Cancelled or empty Title
    if (newTitle.trim() === "") {
      alert("Title cannot be empty.");
      return;
    }

    var checklist = [...this.props.currentTraining.CheckList];
    var idx = checklist.findIndex(i => i.Id === item.Id);
    var steps = [...this.props.currentTraining.Steps];
    var stepIdx = steps.findIndex(s => s.StepType === 3 && s.Step === item.Index);
    var videoIdx = steps.findIndex(s => s.StepType === 4 && s.Step === item.Index);
    if (idx >= 0) {
      checklist[idx] = { ...checklist[idx], Step: newTitle.trim() };
      if(stepIdx >= 0)
        steps[stepIdx] = { ...steps[stepIdx], Title: newTitle.trim() };
      if(videoIdx >= 0)
        steps[videoIdx] = { ...steps[videoIdx], Title: newTitle.trim() };
      const currentTraining = {...this.props.currentTraining, CheckList: checklist, Steps: steps};
      this.props.updateState({currentTraining: currentTraining});
    }
    this.setState({ swipedId: null });
  }

  handleDelete = (item) => {
    if (window.confirm(`Are you sure you want to delete step: ${item.Step}?`)) {
      const list = this.props.currentTraining.CheckList.filter(t => t.Id !== item.Id);
      const steps = this.props.currentTraining.Steps.filter(s => !(s.Step === item.Index && (s.StepType === 3 || s.StepType === 4)));
      const removedSteps = this.props.currentTraining.Steps.length - steps.length;
      for (let i = item.Index + 1; i <= this.props.currentTraining.CheckList.length; i++) {
        const clItem = list.find(c => c.Index === i);
        if (clItem) clItem.Index -= 1;  
        const clStep = steps.find(s => s.Step === i && s.StepType === 3);
        if (clStep) clStep.Step -= 1;
        const vmStep = steps.find(s => s.Step === i && s.StepType === 4);
        if (vmStep) vmStep.Step -= 1;
        if (clStep) clStep.Index -= removedSteps;
        if (vmStep) vmStep.Index -= removedSteps;
      }
      const conclusionSteps = steps.filter(s => s.StepType === 2 && removedSteps.length > 0 && s.Index > removedSteps[0].Index);
      conclusionSteps?.forEach(s => s.Index -= removedSteps);
      const currentTraining = {...this.props.currentTraining, CheckList: list, Steps: steps};

      this.props.updateState({currentTraining: currentTraining});
    }
    this.setState({ swipedId: null });
  }

  handleCreate = () => {
    const newIndex = this.props.currentTraining.CheckList?.length ? Math.max(...this.props.currentTraining.CheckList.map(t => t.Index)) + 1 : 1;
    const newTitle = prompt("Enter new Step:", `New Step ${newIndex}`);
    if (!newTitle) return; // Cancelled or empty Title
    if (newTitle.trim() === "") {
      alert("Title cannot be empty.");
      return;
    }

    const newCheckListItem = { 
      Id: uuid(), 
      Step: newTitle.trim(),
      Index: newIndex};
    const updatedTraining = this.props.currentTraining;
    if(!updatedTraining.CheckList) 
      updatedTraining.CheckList = [];
    updatedTraining.CheckList.push(newCheckListItem);
    const steps = [...updatedTraining.Steps];
    steps.forEach(s => {
      if (s.Step >= newIndex) {
        s.Step += 1;
      }
    });
    updatedTraining.Steps = steps;
    this.props.updateState((prev) => ({
      currentTraining: updatedTraining
    }), () => {
      console.log('Created new step:', newCheckListItem.Step);
    });
  };

  focusInput = (index) => {
    console.log(this.inputRefs[index]); // Debug log to check the ref
    if (this.inputRefs[index]) {
      this.inputRefs[index].focus(); // Focus the input element
    }
  }

  handleFinalize = () =>
  {
    const currentTraining = {...this.props.currentTraining, FinalizedTime: (new Date()).toISOString()}
    this.props.updateState({currentTraining: currentTraining});
    const curURL = new URL(window.location.href);
    this.props.navigate({
      pathname: '/perform-training',
      search: curURL.search
    });
  }
         
  render() {
    console.log("rendering MainAccordion, with props: ", this.props, " state: ", this.state);
    if(!this.props.currentTraining){
      return (<div><p>No training selected.</p></div>);
    }
    var introductionStepFilled = this.state.introductionStep?.Information ||
      this.state.introductionStep?.AudioFilepath || 
      this.state.introductionStep?.ContentImagePath;

    var checklist = this.props.currentTraining?.CheckList;
    var checkListStep = this.props.currentTraining?.Steps?.find(step => step.StepType === 3 && step.Title === 'Preview Checklist');
    var videoModelStep = this.props.currentTraining?.Steps?.find(step => step.StepType === 4 && step.Title === 'Preview Checklist');
    var checklistFilled = [];
    checklistFilled.push((checklist && checklist.length > 0) || 
      checkListStep?.AudioFilepath || 
      videoModelStep?.VideoFilepath);

    checklist.forEach((item) => {
      checkListStep = this.props.currentTraining?.Steps?.find(step => step.StepType === 3 && step.Title === item.Step);
      videoModelStep = this.props.currentTraining?.Steps?.find(step => step.StepType === 4 && step.Title === item.Step);
      checklistFilled.push(checkListStep?.AudioFilepath || videoModelStep?.VideoFilepath);
    })

    checkListStep = this.props.currentTraining?.Steps?.find(step => step.StepType === 3 && step.Title === 'Review Checklist');
    videoModelStep = this.props.currentTraining?.Steps?.find(step => step.StepType === 4 && step.Title === 'Review Checklist');
    checklistFilled.push(checkListStep?.AudioFilepath || videoModelStep?.VideoFilepath);
  
    var curStepAudio = !!this.props.currentStep?.AudioFilepath;
    var curStepImage = !!this.props.currentStep?.ContentImagePath;
    var curStepVideoStep = this.props.currentStep?.Title ? this.props.currentTraining?.Steps?.find(step => step.StepType === 4 && step.Title === this.props.currentStep.Title) : null;
    var curStepVideo = !!curStepVideoStep?.VideoFilepath;

    var trainingFinalized = !!this.props.currentTraining.FinalizedTime;

    var swipedId = this.state.swipedId;

    return (
      // <div className="training-container">
      <div >
        {!this.props.currentTraining && <p>Time to edit the training: No Training Selected'</p>}
        {this.props.currentTraining && (
          <Accordion className="accordion-section" open={this.state.openedTab} toggle={this.toggle} >
            <AccordionItem className="communinication-partners-accordion-item">
              <AccordionHeader className="custom-accordion-header" targetId="1">
                <img src={audienceIcon} alt="Audience Icon" style={{ width: '50px', marginLeft: '10px', marginRight: '10px' }} className={
                  this.props.currentTraining.CommunciationPartners || this.props.currentTraining.SubjectPersona ? 'icon-filled' : 'icon-empty'
                }/>
                {/* <strong style={{fontSize: '2em'}}>List the communication partners who would benefit from instruction</strong> */}
                <strong style={{fontSize: '2em'}}>Describe the communication partners</strong>
              </AccordionHeader>
              <AccordionBody className="custom-accordion-body" accordionId="1">
                {/* <div key={0}>
                  <p style={{ textAlign: 'left' }} onClick={() => this.focusInput(0)}>This training is intended for the communication partners of:</p>
                  <Input innerRef={(el) => (this.inputRefs[0] = el)} size='lg' className="instruct_accordion" type="text" name="SubjectPersona" value={this.props.currentTraining.SubjectPersona || ''} onChange={window.handleTextChange}/>
                </div> */}
                {/* <div key={1}>
                  <p style={{ textAlign: 'left' }} onClick={() => this.focusInput(1)}>AAC users encounter many different communication partners in their daily life (e.g., family, people in the community, etc.). Who are the anticipated communication partners?</p>
                  <Input innerRef={(el) => (this.inputRefs[1] = el)} size='lg' className="instruct_accordion" type="text" name='CommunciationPartners' value={this.props.currentTraining.CommunciationPartners || ''} onChange={window.handleTextChange}/>
                </div> */}
                <div key={1}>
                  <p style={{ textAlign: 'left' }} onClick={() => this.focusInput(1)}>Who are the communication partners who would benefit from the training?</p>
                  <Input innerRef={(el) => (this.inputRefs[1] = el)} size='lg' className="instruct_accordion" type="text" name='CommunciationPartners' value={this.props.currentTraining.CommunciationPartners || ''} onChange={window.handleTextChange}/>
                </div>
              </AccordionBody>
            </AccordionItem>
            <AccordionItem className="strategy-accordion-item">
              <AccordionHeader targetId="2">
                <img src={strategyIcon} alt="Strategy Icon" style={{ width: '50px', marginLeft: '10px', marginRight: '10px' }} className={
                  this.props.currentTraining.Title ? 'icon-filled' : 'icon-empty'
                }/>
                {/* <strong style={{fontSize: '2em'}}>State the strategy or key skill for these communication partners to learn</strong> */}
                <strong style={{fontSize: '2em'}}>State the strategy</strong>
              </AccordionHeader>
              <AccordionBody accordionId="2">
                <div key={2}>
                  {/* <p style={{ textAlign: 'left' }} onClick={() => this.focusInput(2)}>This could be one strategy with multiple steps (e.g., offering choices, using a visual schedule, etc.) or a few key points.</p> */}
                  <p style={{ textAlign: 'left' }} onClick={() => this.focusInput(2)}>What is the strategy or key skill for these partners to learn?</p>
                  <Input innerRef={(el) => (this.inputRefs[2] = el)} size='lg' className="instruct_accordion" type="text" name='Title' value={this.props.currentTraining.Title || ''} onChange={window.handleTextChange}/>
                </div>
              </AccordionBody>
            </AccordionItem>
            <AccordionItem className="introduction-accordion-item">
              <AccordionHeader targetId="3">
                <img src={importantIcon} alt="Important Icon" style={{ width: '50px', marginLeft: '10px', marginRight: '10px' }} className={
                  introductionStepFilled ? 'icon-filled' : 'icon-empty'
                }/>
                {/* <strong style={{fontSize: '2em'}}>Describe why {(this.props.currentTraining.Title) ? this.props.currentTraining.Title : 'this strategy or skill'} is important</strong> */}
                <strong style={{fontSize: '2em'}}>Explain the importance</strong>
              </AccordionHeader>
              <AccordionBody accordionId="3">
                <div key={3}>
                  <p style={{ textAlign: 'left' }} onClick={() => this.focusInput(3)}>What are the benefits for the person who uses AAC? What are the benefits for communication partners?</p>
                  <Input innerRef={(el) => (this.inputRefs[3] = el)} className="instruct_accordion" size={'lg'} type="textarea" name='IntroductionText' rows={3} value={this.state.introductionStep.Information || ''} onChange={window.handleTextChange}/>
                </div>
                <ButtonGroup className="d-flex flex-column flex-lg-row" style={{paddingTop: '10px'}}>
                  <AudioCapturePopup 
                    ref={window.audioRef} 
                    currentTraining={this.props.currentTraining} 
                    currentStep={this.props.currentStep} 
                    updateState={this.props.updateState} 
                    buttonText={`${curStepAudio ? 'Update the': 'Add a'} short audio clip describing why ${(this.props.currentTraining.Title) ? this.props.currentTraining.Title : 'this'} is important`} 
                    buttonClass={curStepAudio ? 'button-content-filled' : 'button-content-empty' }
                  />
                  <Button className={curStepImage ? 'button-content-filled' : 'button-content-empty' } size="lg" onClick={window.openMediaCapture}>{curStepImage ? 'Update the' : 'Add an'} image to illustrate the strategy or skill</Button>
                  <Button className="button-basic" style={{width: '100%'}} size="lg" onClick={this.viewPreview}>Preview content as it will display once training is finalized</Button>
                </ButtonGroup>
              </AccordionBody>
            </AccordionItem>
            <AccordionItem className="checklist-accordion-item">
              <AccordionHeader targetId="4">
                <img src={stepsIcon} alt="Steps Icon" style={{ width: '50px', marginLeft: '10px', marginRight: '10px' }} className={
                  checklistFilled[0] ? 'icon-filled' : 'icon-empty'
                }/>
                {/* <strong style={{fontSize: '2em'}}>List the steps in or key points for {(this.props.currentTraining.Title) ? this.props.currentTraining.Title : 'this stategy or skill'}</strong> */}
                <strong style={{fontSize: '2em'}}>List the strategy steps</strong>
              </AccordionHeader>
              <AccordionBody accordionId="4">
                <p style={{ textAlign: 'left' }}>What are the steps or key points for this strategy or skill?</p>
                {checklist?.map(item => (
                  <div
                    key={item.Id}
                    className={`step-item ${swipedId === item.Id ? 'swiped' : ''}`}
                    onContextMenu={(e) => {
                      e.preventDefault(); // prevent default right-click menu
                      if (item.Id === this.state.swipedId) {
                        this.setState({ swipedId: null });
                      } else {
                        this.setState({ swipedId: item.Id });
                      }
                    }}
                  >
                  <div className="step-content">{item.Index + ". " + item.Step}</div>
                    {this.state.swipedId === item.Id && (
                      <div className="step-actions">
                        <Button color="primary" onClick={(e) => { e.stopPropagation(); this.handleEdit(item); }}>Edit</Button>
                        <Button color="danger" onClick={(e) => { e.stopPropagation(); this.handleDelete(item); }}>Delete</Button>
                      </div>
                    )}
                    {this.state.reordering && (
                      <div className="step-actions">
                        <Button
                          color="primary"
                          disabled={item.Index <= 1}
                          onClick={(e) => { e.stopPropagation(); this.moveUp(item); }}
                          title="Move Up"
                        >
                          &#8593; {/* Up arrow */}
                        </Button>
                        <Button
                          color="primary"
                          disabled={item.Index >= this.props.currentTraining.CheckList.length}
                          onClick={(e) => { e.stopPropagation(); this.moveDown(item); }}
                          title="Move Down"
                        >
                          &#8595; {/* Down arrow */}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
                {/* {this.state.lastTouchEvent && (
                  <div style={{ backgroundColor: '#f0f0f0', padding: '10px', marginBottom: '10px', borderRadius: '4px', fontSize: '0.9em', color: '#333' }}>
                    <strong>Touch Debug:</strong> {this.state.lastTouchEvent}
                  </div>
                )} */}
                <ButtonGroup className="d-flex flex-column flex-lg-row" style={{paddingTop: '10px'}}>
                  <Button className={(checklist && checklist.length > 1) ? 'button-content-filled' : 'button-content-empty'} size="lg" onClick={window.handleCreate}> + Add Step to the checklist </Button>
                  <Button className={this.state.reordering ? 'button-focused' : 'button-basic'} style={{width: '100%'}} size="lg" onClick={() => this.setState(prev => ({ reordering: !prev.reordering, swipedId: null }))}>{this.state.reordering ? 'Done Reordering' : 'Reorder Steps'}</Button>
                  <AudioCapturePopup 
                    ref={window.audioRef} 
                    currentTraining={this.props.currentTraining} 
                    currentStep={this.props.currentStep} 
                    updateState={this.props.updateState} 
                    buttonText={`${curStepAudio ? 'Update the': 'Add a'} short audio clip listing the steps`} 
                    buttonClass={curStepAudio ? 'button-content-filled' : 'button-content-empty' }
                  />
                  <Button className={curStepVideo ? 'button-content-filled' : 'button-content-empty'} size="lg" onClick={window.openMediaCapture}>{curStepVideo ? "Update the" : "Add a"} video to demonstrate the entire strategy</Button>
                </ButtonGroup>
              </AccordionBody>
            </AccordionItem>
            {!checklist?.length && (
              <AccordionItem className="video-model-accordion-item">
                <AccordionHeader targetId="5">
                  <img src={recordIcon} alt="Record Icon" style={{ width: '50px', marginLeft: '10px', marginRight: '10px' }} className={'icon-empty'}/>
                  {/* <strong style={{fontSize: '2em'}}>Add video for steps with the person using AAC</strong> */}
                  <strong style={{fontSize: '2em'}}>Add video for steps</strong>
                </AccordionHeader>
                <AccordionBody accordionId="5">
                  <p style={{ textAlign: 'left' }}>The video will demonstrate what the communication partner should do when they perform a step. Steps need to be set in the previous section before this is available.</p>
                </AccordionBody>
              </AccordionItem>
            )}
            {checklist?.length > 0 && (
              checklist.map((step, index) => (
                <AccordionItem key={step.Id} className="video-model-accordion-item">
                  <AccordionHeader targetId={`${5 + index}`}>
                    <img src={recordIcon} alt="Record Icon" style={{ width: '50px', marginLeft: '10px', marginRight: '10px' }} className={checklistFilled[index + 1] ? 'icon-filled' : 'icon-empty'}/>
                    {/* <strong style={{fontSize: '2em'}}>Add video for Step {index + 1} ({step.Step}) with the person using AAC</strong> */}
                    <strong style={{fontSize: '2em'}}>Add video for {step.Step}</strong>
                  </AccordionHeader>  
                  <AccordionBody accordionId={`${5 + index}`}>
                    <p style={{ textAlign: 'left' }}>The video will demonstrate what the communication partner should do when they {step.Step}</p>
                    <ButtonGroup className="d-flex flex-column flex-lg-row" style={{paddingTop: '10px'}}>
                      <AudioCapturePopup 
                        ref={window.audioRef} 
                        currentTraining={this.props.currentTraining} 
                        currentStep={this.props.currentStep} 
                        updateState={this.props.updateState} 
                        buttonText={`${curStepAudio ? 'Update the': 'Add a'} short audio clip that expains ${step.Step} (optional)`} 
                        buttonClass={curStepAudio ? 'button-content-filled' : 'button-content-empty' }
                      />
                      <Button className={curStepVideo ? 'button-content-filled' : 'button-content-empty'} size="lg" onClick={window.openMediaCapture}>{curStepVideo ? "Update the" : "Add a"} video to demonstrate Step {index + 1} ({step.Step})</Button>
                    </ButtonGroup>
                  </AccordionBody>
                </AccordionItem>
            )))}
            <AccordionItem className="review-accordion-item">
              <AccordionHeader targetId="99">
                <img src={reviewIcon} alt="Review Icon" style={{ width: '50px', marginLeft: '10px', marginRight: '10px' }} className={
                  checklistFilled[checklistFilled.length - 1] ? 'icon-filled' : 'icon-empty'
                }/>
                {/* <strong style={{fontSize: '2em'}}>Review the entire strategy for the communication partner</strong> */}
                <strong style={{fontSize: '2em'}}>Review the strategy</strong>
              </AccordionHeader>
              <AccordionBody accordionId="99">
                {/* <p style={{ textAlign: 'left' }}>Review why the strategy is important. You may want to discuss a way to generalize the strategy or add another example of the strategy being used.</p> */}
                <p style={{ textAlign: 'left' }}>You may want to discuss a way to generalize the strategy or add another example of the strategy being used.</p>
                <ButtonGroup className="d-flex flex-column flex-lg-row" style={{paddingTop: '10px'}}>
                  <AudioCapturePopup 
                    ref={window.audioRef} 
                    currentTraining={this.props.currentTraining} 
                    currentStep={this.props.currentStep} 
                    updateState={this.props.updateState} 
                    buttonText={`${curStepAudio ? 'Update the': 'Add a'} short audio clip to review the strategy & its importance`} 
                    buttonClass={curStepAudio ? 'button-content-filled' : 'button-content-empty' }
                  />
                  <Button className={curStepVideo ? 'button-content-filled' : 'button-content-empty'} size="lg" onClick={window.openMediaCapture}>{curStepVideo ? "Update the" : "Add a"} video to illustrate</Button>
                </ButtonGroup>
              </AccordionBody>
            </AccordionItem>
            <AccordionItem className="finalize-accordion-item">
              <AccordionHeader targetId="100">
                <img src={exportIcon} alt="Review Icon" style={{ width: '50px', marginLeft: '10px', marginRight: '10px' }} className={ trainingFinalized ? 'icon-filled' : 'icon-empty' }/>
                <strong style={{fontSize: '2em'}}>Finalize the training</strong>
              </AccordionHeader>
              <AccordionBody accordionId="100">
                <p style={{ textAlign: 'left' }}>Review the entire training.</p>
                <p style={{ textAlign: 'left' }}>
                  To make changes, return here
                  <ul>
                    <li>use the back button on the browser or</li>
                    <li>Go the the training on the HOME screen and select Edit</li>
                  </ul>
                </p>
                <p style={{ textAlign: 'left' }}>Then use it to teach communication partners!</p>
                <Button className="button-basic" style={{width: '100%'}} size="lg" color="primary" onClick={this.handleFinalize}>Review the training</Button>
              </AccordionBody>
            </AccordionItem>
          </Accordion>
        )}
      </div>
    );
  }
}
export default withNavigation(MainAccordionWithTour);